import {VanillaVoidCallback} from "../VanillaReader/VanillaReaderModel";
import {ITransformRectZoomOptions} from "@colibrio/colibrio-reader-framework/colibrio-readingsystem-base";
import {IRect, LengthUnit} from "@colibrio/colibrio-reader-framework/colibrio-core-base";

/**
 * # VanillaReaderUiGuidedReadingContentMask
 *
 * ## RESPONSIBILITIES
 *
 * This class renders and manages a mask layer that overlays all the content in the window, except for a specified
 * rectangle shape. This rectangle, rendered using a CSS polygon, is "cut out" from the overlay using an
 * `evenodd` CSS rule.
 *
 * By default, the rectangular shape transforms between its shapes using a tween animation. Options for to animate can
 * be specified in the `_transitionOptions` constructor parameter.
 *
 * ## RELATED TYPES
 *
 * - VanillaReaderGuidedReadingPlayer
 * - VanillaReaderUiFabPanelMaskToggle
 *
 **/
export class VanillaReaderUiGuidedReadingContentMask {
    private _style = `
        <style>            
        :root {
              --vanilla-reader__guided-reading-content-mask__clip-box-top: 0;
              --vanilla-reader__guided-reading-content-mask__clip-box-left: 0;
              --vanilla-reader__guided-reading-content-mask__clip-box-bottom: 100%;
              --vanilla-reader__guided-reading-content-mask__clip-box-right: 100%;            

        }
            #vanilla-reader__guided-reading-content-mask {
                display: none;
                position: fixed;
                top: 0;
                left: 0;
                width: 100%;
                height: 100%;
                pointer-events: none;
                touch-action: none;
                background-color: black;
                mix-blend-mode: multiply;                
                z-index: 1;
                clip-path: polygon( evenodd,
                    /* overlay rect */
                    0 0,
                    100% 0,
                    100% 100%,
                    0% 100%,
                    0 0,
                    /* ruler clip rect */
                    var(--vanilla-reader__guided-reading-content-mask__clip-box-left) var(--vanilla-reader__guided-reading-content-mask__clip-box-top),
                    var(--vanilla-reader__guided-reading-content-mask__clip-box-right) var(--vanilla-reader__guided-reading-content-mask__clip-box-top),
                    var(--vanilla-reader__guided-reading-content-mask__clip-box-right) var(--vanilla-reader__guided-reading-content-mask__clip-box-bottom),
                    var(--vanilla-reader__guided-reading-content-mask__clip-box-left) var(--vanilla-reader__guided-reading-content-mask__clip-box-bottom),
                    var(--vanilla-reader__guided-reading-content-mask__clip-box-left) var(--vanilla-reader__guided-reading-content-mask__clip-box-top)             
                );
                opacity: 0;                
            }

        </style>
    `;
    private _template = `
        <div id="vanilla-reader__guided-reading-content-mask" aria-hidden="true"></div>
    `;

    private _contentMaskElement: HTMLElement;
    private _currentMaskedRect: IRect | undefined;
    private _onTransitionStartedCallback: VanillaVoidCallback | undefined;
    private _onTransitionEndedCallback: VanillaVoidCallback | undefined;

    private _animationDuration: number = 600;
    private _animate: boolean = true;

    private _initialHostElementBackgroundColor: string;

    constructor(
        private _hostElement: HTMLElement,
        private _readerViewElement: HTMLElement,
        insertPosition: InsertPosition = 'afterbegin',
        private _transitionOptions: ITransformRectZoomOptions,
        private _backgroundColor: string = '#000000'
        ) {

        this._animationDuration = this._transitionOptions.animationOptions?.durationMs || this._animationDuration;
        this._hostElement.insertAdjacentHTML(insertPosition, this._style);
        this._hostElement.insertAdjacentHTML(insertPosition, this._template);

        this._initialHostElementBackgroundColor = this._hostElement.style.backgroundColor;

        this._contentMaskElement = this._hostElement.querySelector('#vanilla-reader__guided-reading-content-mask')!;
    }

    public get backgroundColor(): string {
        return this._backgroundColor;
    }

    public set backgroundColor(color: string) {
        this._backgroundColor = color;
        this._hostElement.style.backgroundColor = this._backgroundColor;
    }

    public get isVisible(): boolean {
        return this._contentMaskElement.style.display === 'block';
    }

    public get currentMaskedRect(): IRect | undefined{
        return this._currentMaskedRect;
    }

    onTransitionStarted(callback: VanillaVoidCallback): void {
        this._onTransitionStartedCallback = callback;
    }

    onTransitionEnded(callback: VanillaVoidCallback): void {
        this._onTransitionEndedCallback = callback;
    }

    show(): void {
        this._hostElement.style.backgroundColor = this._backgroundColor;
        this._contentMaskElement.style.display = 'block';
        this._contentMaskElement.style.opacity = '1';
    }

    hide(): void {
        this._hostElement.style.backgroundColor = this._initialHostElementBackgroundColor;
        this._contentMaskElement.style.opacity = '0';
        setTimeout(()=>{
            this._contentMaskElement.style.display = 'none';
        }, this._animationDuration);
    }

    resizeRelative(sourceRect: IRect, visiblePageRect: IRect, clampToCenter: boolean = true, transformOptions: ITransformRectZoomOptions): void {
        if(this._onTransitionStartedCallback) {
            this._onTransitionStartedCallback();
        }

        this._currentMaskedRect = sourceRect;

        let targetHeight = (sourceRect.height / 100) * visiblePageRect.height;
        let targetWidth = (sourceRect.width / 100) * visiblePageRect.width;
        let targetX = (sourceRect.x / 100) * visiblePageRect.width + visiblePageRect.x;
        let targetY = (sourceRect.y / 100) * visiblePageRect.height + visiblePageRect.y;

        if(clampToCenter) {
            targetX = (this._readerViewElement.clientWidth - targetWidth) / 2;
            targetY = (this._readerViewElement.clientHeight - targetHeight) / 2;
        }

        let targetRect = new DOMRect(targetX, targetY, targetWidth, targetHeight);

        this._moveAndScaleMaskRect(targetRect, LengthUnit.CSS_PIXELS, transformOptions);

        setTimeout(()=>{
            if(this._onTransitionEndedCallback) {
                this._onTransitionEndedCallback();
            }
        }, transformOptions.animationOptions?.durationMs || 600);

    }

    /**
     *
     * PRIVATE METHODS
     *
     * */

    _moveAndScaleMaskRect(rect: DOMRect, unit: LengthUnit = LengthUnit.CSS_PIXELS, transformOptions: ITransformRectZoomOptions): void {
        let unitStr = unit === LengthUnit.PERCENT ? '%' : 'px';

        let durationMs = transformOptions.animationOptions?.durationMs ?? this._animationDuration;

        if(this._animate) {
            this._contentMaskElement.style.transition = `clip-path ${durationMs}ms ease-in-out 0s, opacity ${durationMs}ms ease 0s`;
        } else {
            this._contentMaskElement.style.transition = `opacity ${durationMs}ms ease 0s`;
        }

        this._contentMaskElement.style.setProperty('--vanilla-reader__guided-reading-content-mask__clip-box-left', `${rect.left}${unitStr}`);
        this._contentMaskElement.style.setProperty('--vanilla-reader__guided-reading-content-mask__clip-box-top', `${rect.top + 1}${unitStr}`);
        this._contentMaskElement.style.setProperty('--vanilla-reader__guided-reading-content-mask__clip-box-right', `${rect.right}${unitStr}`);
        this._contentMaskElement.style.setProperty('--vanilla-reader__guided-reading-content-mask__clip-box-bottom', `${rect.bottom - 1}${unitStr}`);
    }

}
